var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import Logo from '@doordash/component-logo';
import { Icon, InlineChildren, StackChildren, Text, } from '@doordash/design-language';
import Button from '@doordash/component-button';
import { QuizScreenContainer, FullScreenContainer, BackContainer, ActionAndProgressContainer, BackAndProgressContainer, } from './QuizStyles';
import { isEmailFormatValid } from '../../../utils/validateEmailHelper';
import { useMediaQuery } from '../../../components/Common/MediaQueryProvider';
import LocationPathHelper from '../../../utils/location_path_helper';
import { selfServeRoutes } from '../../../components/AppStructure/PublicRoutes';
import { WorkBenefit } from '../../../types/GloblalTypes';
import track from '../../../utils/track';
import { QuizEvents } from '../../../constants/events';
import { HowOftenResponseId, ProductOffering, QuizQuestionsData, RecommendedProducts, WhereIsTeamResponseId, } from './QuizPageData';
import { QuizPageStrings } from './QuizStrings';
import { EmailInputSection, OptionsSelectionSection, ProgressBar, RecommendationTile, } from './components';
var PRODUCT_OPTIONS_VERSION = 'quiz';
export var Quiz = function () {
    var _a = __read(React.useState(''), 2), recommendedProduct = _a[0], setRecommendedProduct = _a[1];
    var _b = __read(React.useState(0), 2), currentQuizStep = _b[0], setCurrentQuizStep = _b[1];
    var _c = __read(React.useState({}), 2), quizResponses = _c[0], setQuizResponses = _c[1];
    var _d = __read(React.useState(''), 2), emailError = _d[0], setEmailError = _d[1];
    var isMobile = useMediaQuery().isMobile;
    var history = useHistory();
    React.useEffect(function () {
        // on page load
        track(QuizEvents.PAGE_VIEW, {
            page_version: PRODUCT_OPTIONS_VERSION,
        });
    }, []);
    var getPageTitle = function () {
        if (recommendedProduct) {
            return QuizPageStrings.pageTitle.recommendation();
        }
        return QuizPageStrings.pageTitle.shortQuiz();
    };
    var getButtonText = function () {
        if (recommendedProduct) {
            return QuizPageStrings.actionButton.startOver();
        }
        if (hasNextQuestion()) {
            return QuizPageStrings.actionButton.continue();
        }
        return QuizPageStrings.actionButton.finish();
    };
    var hasNextQuestion = function () {
        return currentQuizStep < QuizQuestionsData.length - 1;
    };
    var hasBack = function () {
        return currentQuizStep > 0;
    };
    var resetQuiz = function () {
        setRecommendedProduct(null);
        setQuizResponses({});
        setCurrentQuizStep(0);
        setEmailError(''); // should not be necessary but reset anyway for clarity
    };
    var onResponseProvided = function (response) {
        var clonedQuizResponses = __assign({}, quizResponses);
        clonedQuizResponses[currentQuizStep] = response;
        setQuizResponses(clonedQuizResponses);
    };
    var onNextOrFinishOrStartOverClick = function () {
        if (recommendedProduct) {
            // when on recommended program, button text is start over so we should reset quiz
            track(QuizEvents.START_OVER_CLICK);
            return resetQuiz();
        }
        if (hasNextQuestion()) {
            var currentQuestion = QuizQuestionsData[currentQuizStep];
            var optionResponse = quizResponses[currentQuizStep];
            track(QuizEvents.OPTION_RESPONSE_SUBMIT, {
                question: currentQuestion.id,
                response: optionResponse,
            });
            // move to next step, if there is next step
            return setCurrentQuizStep(currentQuizStep + 1);
        }
        // currently on work email input step
        // check email format
        var emailProvided = quizResponses[currentQuizStep];
        var isEmailValid = isEmailFormatValid(emailProvided);
        if (!isEmailValid) {
            // invalid email format
            return setEmailError('Not a valid email');
        }
        // valid email format, calculate recommendation
        track(QuizEvents.EMAIL_SUBMIT, {
            email: emailProvided,
        });
        setEmailError('');
        if ((quizResponses === null || quizResponses === void 0 ? void 0 : quizResponses[0]) === WhereIsTeamResponseId.inOffice &&
            ((quizResponses === null || quizResponses === void 0 ? void 0 : quizResponses[1]) === HowOftenResponseId.oneTime ||
                (quizResponses === null || quizResponses === void 0 ? void 0 : quizResponses[1]) === HowOftenResponseId.monthly)) {
            // recommend catering for "inOffice" and "oneTime"/"monthly"
            setRecommendedProduct(ProductOffering.catering);
        }
        else {
            setRecommendedProduct(ProductOffering.expensedMeals);
        }
    };
    var onBackClick = function () {
        var currentQuestion = QuizQuestionsData[currentQuizStep];
        track(QuizEvents.BACK_CLICK, {
            question: currentQuestion.id,
        });
        if (recommendedProduct) {
            return setRecommendedProduct(null);
        }
        return setCurrentQuizStep(currentQuizStep - 1);
    };
    var onRecommendedProductActionClick = function () {
        if (recommendedProduct === ProductOffering.catering) {
            track(QuizEvents.CATERING_ORDER_NOW_CLICK, {}, {}, function () {
                // redirect to catering link
                window.location.href =
                    RecommendedProducts[recommendedProduct].button.link;
            });
            return;
        }
        // redirect to expensed meals self serve
        track(QuizEvents.EM_ENROLL_NOW_CLICK);
        var search = LocationPathHelper.getWindowLocationSearch() || '';
        var urlQueryString = search;
        urlQueryString = urlQueryString + "&workBenefit=" + WorkBenefit.WORK_BENEFIT_EXPENSED_MEALS;
        history.push({
            pathname: selfServeRoutes.SIGN_UP_TEAM_ROUTE,
            search: urlQueryString,
        });
    };
    return (React.createElement(FullScreenContainer, null,
        React.createElement(QuizScreenContainer, null,
            React.createElement(StackChildren, null,
                React.createElement(Logo, { type: Logo.Types.LogoMark, size: Logo.Sizes.Large }),
                React.createElement(InlineChildren, { justifyContent: InlineChildren.Justification.Center },
                    React.createElement(Text, { tag: "h1", styles: Text.Styles.PageTitle2 }, getPageTitle())),
                recommendedProduct ? (React.createElement(InlineChildren, { justifyContent: InlineChildren.Justification.Center },
                    React.createElement(RecommendationTile, { product: RecommendedProducts[recommendedProduct], onActionClick: onRecommendedProductActionClick }))) : (React.createElement(React.Fragment, null,
                    React.createElement(InlineChildren, { justifyContent: InlineChildren.Justification.Center },
                        React.createElement(Text, { color: Text.Colors.TextSecondary, styles: Text.Styles.Medium24 }, QuizQuestionsData[currentQuizStep].question)),
                    QuizQuestionsData[currentQuizStep].options && (React.createElement(OptionsSelectionSection, { isMobile: isMobile, options: QuizQuestionsData[currentQuizStep].options, selectedOption: quizResponses[currentQuizStep], onOptionSelected: onResponseProvided })),
                    QuizQuestionsData[currentQuizStep].email && (React.createElement(EmailInputSection, { emailLabel: QuizQuestionsData[currentQuizStep].email.label, emailValue: quizResponses[currentQuizStep], emailError: emailError, onEmailChange: onResponseProvided })))),
                React.createElement(ActionAndProgressContainer, null,
                    React.createElement(BackAndProgressContainer, null,
                        hasBack() && (React.createElement(BackContainer, { onClick: onBackClick },
                            React.createElement(Icon, { type: Icon.Types.ArrowLeft, size: Icon.Sizes.Medium, color: Icon.Colors.CurrentColor, accessibilityLabel: "Back" }))),
                        !recommendedProduct && (React.createElement(ProgressBar, { currentQuizStep: currentQuizStep, numberOfQuestions: QuizQuestionsData.length }))),
                    React.createElement("div", null,
                        React.createElement(Button, { size: Button.Sizes.Small, isInline: true, onClick: onNextOrFinishOrStartOverClick, isDisabled: quizResponses[currentQuizStep] == undefined, type: recommendedProduct
                                ? Button.Types.Tertiary
                                : Button.Types.Primary }, getButtonText())))))));
};
export default Quiz;
